// Operations → Workflows. Work in flight, and where each piece has reached.
//
// §7.3 REJECTS THE BOARD, NOT THE DATA
// ====================================
// `/pipeline` was a Kanban board and §7.3 explicitly rejects a PM/kanban product;
// §3.12 leaves the board intentionally homeless. Its DATA relocates here. So this
// surface reads the velocity endpoints that described pipeline movement and
// presents them as movement, not as columns to drag.
//
// THE VELOCITY ENDPOINTS ARE LOAD-BEARING ELSEWHERE
// =================================================
// /api/velocity/metrics and /api/velocity/trends are consumed by the Brain and
// must keep working. Nothing here writes to them, and no backend file is touched
// by this branch at all, so they cannot be broken from this lane by construction.
// Removing the /pipeline *page* is in scope; the endpoints are not, and neither
// is the page's file (R3 — nothing is deleted).
import React from "react";

import PageHead from "../../components/PageHead";
import Panel, { Figure, Rows } from "./Panel";
import { READY, metric, useEndpoint } from "./useEndpoint";

export default function Workflows() {
  const metrics = useEndpoint("/velocity/metrics");
  const trends = useEndpoint("/velocity/trends");
  const checkpoints = useEndpoint("/agent-checkpoints");

  const trendRows = Array.isArray(trends.data?.trends)
    ? trends.data.trends
    : Array.isArray(trends.data)
    ? trends.data
    : [];
  const checkpointRows = Array.isArray(checkpoints.data?.checkpoints)
    ? checkpoints.data.checkpoints
    : Array.isArray(checkpoints.data)
    ? checkpoints.data
    : [];

  return (
    <>
      <PageHead
        title="Workflows"
        subtitle="Work in flight, and where each piece has reached."
      />

      <div className="grid op-grid">
        <Panel
          title="Velocity"
          state={metrics}
          empty={metrics.status === READY && !metrics.data}
          note="/api/velocity/metrics returned nothing to summarise."
        >
          <Figure label="Metrics" value={metric(
            typeof metrics.data === "object" && metrics.data
              ? Object.keys(metrics.data).length
              : null
          )} />
        </Panel>

        <Panel
          title="Trend"
          state={trends}
          empty={trendRows.length === 0}
          note="/api/velocity/trends returned no series."
        >
          <Rows
            items={trendRows.slice(0, 12).map((t, i) => [
              String(t?.period ?? t?.date ?? t?.label ?? `point ${i + 1}`),
              String(t?.value ?? t?.count ?? "—"),
            ])}
          />
        </Panel>

        <Panel
          title="Checkpoints"
          state={checkpoints}
          empty={checkpointRows.length === 0}
          note="/api/agent-checkpoints reported nothing in flight."
        >
          <Rows
            items={checkpointRows.slice(0, 12).map((c, i) => [
              String(c?.name ?? c?.checkpoint ?? c?.id ?? `checkpoint ${i + 1}`),
              String(c?.status ?? c?.state ?? "—"),
            ])}
          />
        </Panel>
      </div>
    </>
  );
}
